import { useEffect, useState } from "react";

const CHOICE_COLORS = ["#ff3cac", "#00f5ff", "#ffe600", "#3dffa0"];
const CHOICE_GLOWS  = ["#ff3cac66", "#00f5ff66", "#ffe60066", "#3dffa066"];

export default function QuestPlayer({ questData, playerName, onVote }) {
  const [voted, setVoted] = useState(null); // index of chosen option

  const phase      = questData?.phase ?? "waiting";
  const text       = questData?.text ?? "";
  const choices    = questData?.choices ?? [];
  const voteCounts = questData?.vote_counts ?? {};
  const totalVoted = questData?.total_voted ?? 0;
  const totalPlayers = questData?.total_players ?? 0;
  const winningIndex = questData?.winning_index ?? -1;
  const scores     = questData?.scores ?? {};
  const step       = questData?.step ?? 0;
  const endTitle   = questData?.title ?? "";
  const myScore    = scores[playerName] ?? 0;

  // Reset/restore the local vote when a new node arrives or after reconnect.
  const [lastStep, setLastStep] = useState(-1);
  useEffect(() => {
    if (phase !== "vote") return;
    if (step !== lastStep) setLastStep(step);
    setVoted(questData?.already_voted ? (questData?.my_vote ?? "__voted__") : null);
  }, [phase, step, questData?.already_voted, questData?.my_vote, lastStep]);

  function handleVote(idx) {
    if (voted !== null) return;
    if (phase !== "vote") return;
    setVoted(idx);
    onVote(idx);
  }

  // ── WAITING ───────────────────────────────────────────────────────────────
  if (phase === "waiting") {
    return (
      <div style={{ textAlign: "center", padding: "2rem 1rem" }}>
        <div style={{ fontSize: "3rem", marginBottom: "0.75rem" }}>🔍</div>
        <p style={{ fontFamily: "'Fredoka One', sans-serif", fontSize:"var(--fs-xl)", color: "#9b5de5" }}>
          Квест начинается...
        </p>
      </div>
    );
  }

  // ── ENDING ────────────────────────────────────────────────────────────────
  if (phase === "ending") {
    return (
      <div style={{ textAlign: "center", padding: "2rem 1rem" }}>
        <div style={{ fontSize: "3rem", marginBottom: "0.5rem" }}>🏁</div>
        <p style={{
          fontFamily: "'Fredoka One', sans-serif", fontSize:"var(--fs-xl)",
          color: "#ffe600", marginBottom: "0.5rem",
        }}>
          {endTitle}
        </p>
        <p style={{
          fontFamily: "'Nunito', sans-serif", color: "#888",
          fontSize:"var(--fs-sm)", marginBottom: "1rem",
        }}>
          {text.length > 120 ? text.slice(0, 120) + "…" : text}
        </p>
        <p style={{
          fontFamily: "'Fredoka One', sans-serif", color: "#ffe600", fontSize:"var(--fs-xl)",
        }}>
          Твои очки: {myScore}
        </p>
      </div>
    );
  }

  // ── RESULT — show what was chosen ─────────────────────────────────────────
  if (phase === "result") {
    const myVoteWon = voted === winningIndex;
    return (
      <div style={{ padding: "1.5rem 1rem", display: "flex", flexDirection: "column", gap: "1rem" }}>
        {/* Result banner */}
        <div style={{
          textAlign: "center",
          background: myVoteWon ? "#3dffa011" : "#ffffff08",
          border: `2px solid ${myVoteWon ? "#3dffa0" : "#ffffff15"}`,
          borderRadius: "1rem", padding: "0.8rem",
        }}>
          <p style={{
            fontFamily: "'Fredoka One', sans-serif", fontSize:"var(--fs-lg)",
            color: myVoteWon ? "#3dffa0" : "#888", margin: 0,
          }}>
            {voted === null
              ? "😶 Ты не успел проголосовать"
              : myVoteWon
              ? "✅ Твой выбор победил! +10 очков"
              : "❌ Выбрало большинство другое"}
          </p>
        </div>

        {/* Winning choice */}
        <div style={{
          background: "#ffe60011", border: "2px solid #ffe60066",
          borderRadius: "0.75rem", padding:"var(--pad-sm) var(--pad-md)",
        }}>
          <p style={{
            fontFamily: "'Nunito', sans-serif", color: "#ffe600",
            fontSize:"var(--fs-sm)", margin: "0 0 0.2rem",
          }}>
            Выбор компании:
          </p>
          <p style={{
            fontFamily: "'Nunito', sans-serif", color: "#fff",
            fontSize:"var(--fs-md)", margin: 0, fontWeight: 700,
          }}>
            {choices[winningIndex]?.text ?? ""}
          </p>
        </div>

        {/* Vote counts */}
        <div style={{ display: "flex", flexDirection: "column", gap: "0.35rem" }}>
          {choices.map((c, i) => {
            const count = voteCounts[i] || 0;
            const isWin = i === winningIndex;
            return (
              <div key={i} style={{
                display: "flex", justifyContent: "space-between",
                fontFamily: "'Nunito', sans-serif", fontSize:"var(--fs-sm)",
                color: isWin ? "#fff" : "#555",
                padding: "0.2rem 0",
              }}>
                <span>{isWin ? "✅ " : ""}{c.text}</span>
                <span style={{ color: CHOICE_COLORS[i % CHOICE_COLORS.length] }}>
                  {count > 0 ? count : "-"}
                </span>
              </div>
            );
          })}
        </div>

        <p style={{
          fontFamily: "'Nunito', sans-serif", color: "#333",
          fontSize:"var(--fs-sm)", textAlign: "center",
        }}>
          Очки: <span style={{ color: "#ffe600" }}>{myScore}</span>
        </p>
      </div>
    );
  }

  // ── VOTE PHASE — main interaction ─────────────────────────────────────────
  return (
    <div style={{ padding: "0.75rem", display: "flex", flexDirection: "column", gap: "0.75rem" }}>

      {/* Scene text — abbreviated */}
      <div style={{
        background: "#0d0d1f", border: "1.5px solid #ffffff0f",
        borderRadius: "0.75rem", padding: "0.75rem",
      }}>
        <p style={{
          fontFamily: "'Nunito', sans-serif",
          fontSize:"var(--fs-sm)", color: "#bbb",
          lineHeight: 1.55, margin: 0,
        }}>
          {text.length > 200 ? text.slice(0, 200) + "…" : text}
        </p>
      </div>

      {/* Voting status */}
      {voted !== null ? (
        <div style={{
          textAlign: "center", padding: "0.5rem",
          fontFamily: "'Fredoka One', sans-serif",
          fontSize:"var(--fs-md)", color: "#3dffa0",
        }}>
          ✅ Ты проголосовал · {totalVoted}/{totalPlayers} готовы
        </div>
      ) : (
        <div style={{
          textAlign: "center",
          fontFamily: "'Fredoka One', sans-serif",
          fontSize:"var(--fs-md)", color: "#ffe600",
        }}>
          Выбери действие:
        </div>
      )}

      {/* Choice buttons */}
      <div style={{ display: "flex", flexDirection: "column", gap:"var(--gap-md)" }}>
        {choices.map((c, i) => {
          const isChosen = voted === i;
          const isOther  = voted !== null && voted !== i;
          const color    = CHOICE_COLORS[i % CHOICE_COLORS.length];
          const glow     = CHOICE_GLOWS[i % CHOICE_GLOWS.length];

          return (
            <button
              key={i}
              onPointerDown={() => handleVote(i)}
              disabled={voted !== null}
              style={{
                background: isChosen ? color + "22" : isOther ? "#ffffff04" : "#ffffff08",
                border: `2px solid ${isChosen ? color : isOther ? "#ffffff0a" : color + "66"}`,
                borderRadius: "0.9rem",
                padding:"var(--pad-md)",
                textAlign: "left",
                cursor: voted !== null ? "default" : "pointer",
                fontFamily: "'Nunito', sans-serif",
                fontSize:"var(--fs-md)",
                color: isOther ? "#333" : "#fff",
                fontWeight: isChosen ? 700 : 400,
                boxShadow: isChosen ? `0 0 16px ${glow}` : "none",
                transform: isChosen ? "scale(1.01)" : "scale(1)",
                transition: "all 0.15s",
                touchAction: "manipulation",
                userSelect: "none",
                WebkitUserSelect: "none",
              }}
            >
              <span style={{
                display: "inline-block", width: "1.4rem",
                color, fontWeight: 700,
              }}>
                {isChosen ? "✓" : `${i + 1}.`}
              </span>
              {c.text}
            </button>
          );
        })}
      </div>

      <p style={{
        fontFamily: "'Nunito', sans-serif", color: "#333",
        fontSize:"var(--fs-sm)", textAlign: "center", margin: 0,
      }}>
        Очки: <span style={{ color: "#ffe600" }}>{myScore}</span>
      </p>
    </div>
  );
}
